import { NavigationConfig } from './types';
import { useParams } from 'react-router-dom';
import { buildAdminUrl } from '../../../utils/adminUrl';
import {
  ADMIN_PAGES_UNRESOLVED,
  AdminPagesEnabled,
  adminSetupUrls,
} from '../../../utils/adminSetupPages';
import type { RbacClaim } from '../../../../models/Rbac';
import { canSeeDataStudio, isV2User } from '../../../utils/rbac';

/**
 * Where a model's audience and conversion goal are configured.
 *
 * Both live in the admin app, not in Studio, and until the Setup section
 * existed the only way to reach them was to open a model and find the links on
 * its metadata card. They decide what every model in the org predicts and who
 * it predicts it for, so they belong beside Computations and Aggregations
 * rather than three clicks inside one model.
 *
 * Each screen has two generations and the newer one is behind a flag the admin
 * app owns, so the path is chosen rather than fixed. `adminSetupPages.ts` holds
 * the pairing and the reason; `ModelSetupLinks` resolves the same answer for
 * the model page's own chips.
 *
 * Empty when no admin base URL is configured for this build, so a local stack
 * without one gets no rows rather than links to nowhere.
 */
function setupSection(
  tenantNumber: number | null,
  adminPages: AdminPagesEnabled
) {
  if (!tenantNumber || !NEW_ADMIN_BASE_URL) return [];
  const urls = adminSetupUrls(tenantNumber, adminPages);
  return [
    {
      label: 'SETUP',
      id: 'sidebar-section-setup',
      items: [
        {
          label: 'Audiences',
          href: urls.audience,
          icon: 'far fa-users',
          isExternal: true,
          description: 'Who each model is trained and scored on',
          id: 'sidebar-link-audiences',
        },
        {
          label: 'Conversions',
          href: urls.conversion,
          icon: 'far fa-bullseye-arrow',
          isExternal: true,
          description: 'What each model counts as a conversion',
          id: 'sidebar-link-conversions',
        },
      ],
    },
  ];
}

export function useAppNavConfig(options?: {
  isSuperKudu?: boolean;
  tenant?: string;
  /**
   * RBAC v2 claims (RGI-1042). Pass `store.rbac` from the caller. v1 users
   * (rbac null) and SuperKudu bypass module/tier filters via the predicates.
   */
  rbac?: RbacClaim | null;
  /**
   * `studio-tree-insights`. Adds the Setup section, and nothing else: off, this
   * hook returns byte for byte what it returned before the section existed.
   */
  revamped?: boolean;
  /**
   * Which generation of the admin app's audience and conversion screens this
   * tenant can open. Only the Setup section reads it, and that section only
   * exists behind `revamped`, so a caller that does not resolve it gets the
   * pages that need no flag.
   */
  adminPages?: AdminPagesEnabled;
}): NavigationConfig {
  const paramsTenant = useParams<{ tenant: string }>().tenant;
  // Use tenant from options if provided (for SSR compatibility), otherwise use params
  const tenant = options?.tenant || paramsTenant;
  const tenantPath = tenant ? `/tenant/${tenant}` : '';
  const tenantNumber = tenant ? Number(tenant) : null;

  // RBAC v2 filters (RGI-1042 / RGI-1115). v1 users (no rbac) and SuperKudu
  // bypass these checks via the predicates. Visibility follows user
  // assignment (not just org purchase) so users only see modules they can
  // actually reach.
  const rbac = options?.rbac ?? null;
  const superKudu = options?.isSuperKudu ?? false;
  const adminPages = options?.adminPages ?? ADMIN_PAGES_UNRESOLVED;

  // V2 users get a flat, single-sidebar nav matching the design mock:
  // Models / Computations / Aggregations + Help & Support. No DATA STUDIO
  // section label, no Back to Admin (the app switcher covers cross-module
  // nav), no PROSPECTING / Open Sales Copilot promo, no Settings bottom link.
  if (isV2User(rbac)) {
    return getV2NavConfig({
      tenantPath,
      tenantNumber,
      revamped: options?.revamped ?? false,
      adminPages,
      showModels: canSeeDataStudio(rbac, { superKudu }),
    });
  }

  const showModels = canSeeDataStudio(rbac, { superKudu });

  return {
    sections: [
      {
        label: 'DATA STUDIO',
        id: 'sidebar-section-data-studio',
        items: [
          // Models hidden for v2 users not assigned to `data_studio` —
          // both Standard Users and Org Admins. Computations + Aggregations
          // stay visible so Org Admins of Sales-Copilot orgs can still
          // configure the shared scoring infra. v1 / SuperKudu always see it.
          ...(showModels
            ? [
                {
                  label: 'Models',
                  href: `${tenantPath}/models`,
                  icon: 'fa-light fa-table-cells',
                  activeMatcher: (pathname: string) =>
                    pathname.startsWith(`${tenantPath}/models`),
                  description: 'Build and manage your prediction models',
                  id: 'sidebar-link-models',
                },
              ]
            : []),
          {
            label: 'Computations',
            href: `${tenantPath}/computations`,
            icon: 'fa-light fa-calculator',
            activeMatcher: (pathname) =>
              pathname.startsWith(`${tenantPath}/computations`),
            description: 'Manage your data computations and transformations',
            id: 'sidebar-link-computations',
          },
          {
            label: 'Aggregations',
            href: `${tenantPath}/aggregations`,
            icon: 'far fa-layer-group',
            activeMatcher: (pathname) =>
              pathname.startsWith(`${tenantPath}/aggregations`),
            description: 'Configure and manage data aggregations',
            id: 'sidebar-link-aggregations',
          },
        ],
      },
      // Off the flag this spreads nothing, so the v1 nav is unchanged.
      ...(options?.revamped ? setupSection(tenantNumber, adminPages) : []),
    ],
    bottomLinks: [
      // One row, not two. Back to Admin and Settings both opened the same admin
      // app, one at its home and one at the profile page inside it, which made the
      // rail look like it led to two places when it led to one. Back to Admin also
      // sat at the top of the rail pointing left, a few pixels from the browser's
      // own back button, so it read as "up one level" and was clicked by people who
      // only wanted to get from a model back to the models list. This is that single
      // destination, for every tenant (RGI-2622), not only those on the revamp. It
      // lands on the admin's settings rather than its home, per product.
      //
      // It keeps `sidebar-link-settings` because the e2e suite locates rows by id and
      // this is still the same row in the same place, renamed.
      tenantNumber
        ? {
            label: 'Admin Center',
            href: buildAdminUrl(tenantNumber, '/settings/profile'),
            icon: 'fa-light fa-gear',
            onClick: () => {
              window.location.href = buildAdminUrl(
                tenantNumber,
                '/settings/profile'
              );
            },
            isExternal: false,
            description: 'Open the HG admin app',
            id: 'sidebar-link-settings',
          }
        : {
            label: 'Settings',
            href: `${tenantPath}/settings`,
            icon: 'fa-light fa-gear',
            isExternal: false,
            description: 'Configure account and organization settings',
            id: 'sidebar-link-settings',
          },
      {
        label: 'Help & Support',
        href: tenantNumber
          ? buildAdminUrl(tenantNumber, '/help')
          : `${tenantPath}/helpCenter`,
        icon: 'far fa-question-circle',
        onClick: tenantNumber
          ? () => {
              window.location.href = buildAdminUrl(tenantNumber, '/help');
            }
          : undefined,
        isExternal: false,
        description: 'Get help and contact support',
        id: 'sidebar-link-help-support',
      },
    ],
  };
}

/**
 * Flat single-sidebar nav for v2 RBAC users. Models requires the data_studio
 * module; Computations + Aggregations stay visible for Org Admins with partial
 * access. Cross-app navigation (Admin Hub, etc.) is owned by the v2 app
 * switcher in the topbar.
 */
function getV2NavConfig(options: {
  tenantPath: string;
  tenantNumber: number | null;
  showModels: boolean;
  revamped: boolean;
  adminPages: AdminPagesEnabled;
}): NavigationConfig {
  const { tenantPath, tenantNumber, showModels, revamped, adminPages } =
    options;

  return {
    sections: [
      {
        items: [
          ...(showModels
            ? [
                {
                  label: 'Models',
                  href: `${tenantPath}/models`,
                  icon: 'fa-light fa-table-cells',
                  activeMatcher: (pathname: string) =>
                    pathname.startsWith(`${tenantPath}/models`),
                  description: 'Build and manage your prediction models',
                  id: 'sidebar-link-models',
                },
              ]
            : []),
          {
            label: 'Computations',
            href: `${tenantPath}/computations`,
            icon: 'fa-light fa-calculator',
            activeMatcher: (pathname) =>
              pathname.startsWith(`${tenantPath}/computations`),
            description: 'Manage your data computations and transformations',
            id: 'sidebar-link-computations',
          },
          {
            label: 'Aggregations',
            href: `${tenantPath}/aggregations`,
            icon: 'far fa-layer-group',
            activeMatcher: (pathname) =>
              pathname.startsWith(`${tenantPath}/aggregations`),
            description: 'Configure and manage data aggregations',
            id: 'sidebar-link-aggregations',
          },
        ],
      },
      ...(revamped ? setupSection(tenantNumber, adminPages) : []),
    ],
    bottomLinks: [
      {
        label: 'Help & Support',
        href: tenantNumber
          ? buildAdminUrl(tenantNumber, '/help')
          : `${tenantPath}/helpCenter`,
        icon: 'far fa-question-circle',
        onClick: tenantNumber
          ? () => {
              window.location.href = buildAdminUrl(tenantNumber, '/help');
            }
          : undefined,
        isExternal: false,
        description: 'Get help and contact support',
        id: 'sidebar-link-help-support',
      },
    ],
  };
}
